import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Sparkles, Check, X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Subscription() {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [processing, setProcessing] = useState(false);

  const load = async () => {
    const [s, p, pay] = await Promise.all([
      api.get("/subscription"),
      api.get("/plans/public"),
      api.get("/subscription/payments"),
    ]);
    setSub(s.data);
    setPlans(p.data);
    setPayments(pay.data);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (plan_code) => {
    setProcessing(true);
    try {
      const { data: order } = await api.post("/subscription/checkout", { plan_code });
      // In stub mode: auto-verify to simulate successful payment
      if (order.is_stub) {
        toast.info("Stub payment mode — simulating successful payment…");
      }
      await api.post("/subscription/verify", {
        order_id: order.order_id,
        payment_id: `pay_stub_${Math.random().toString(36).slice(2, 10)}`,
        signature: null,
        plan_code,
      });
      toast.success(`Subscribed to ${plan_code}!`);
      await load();
    } catch (e) {
      toast.error("Payment failed");
    }
    setProcessing(false);
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? Access continues until the end of the current period.")) return;
    await api.post("/subscription/cancel");
    toast.success("Subscription cancelled");
    load();
  };

  if (!sub) return <div className="text-muted-foreground text-sm">Loading…</div>;

  const statusColor = { trial: "secondary", active: "default", cancelled: "outline", expired: "destructive", suspended: "destructive", past_due: "destructive" }[sub.status] || "outline";

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Subscription</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your plan and billing history</p>
      </div>

      {/* Current status */}
      <Card className="p-6" data-testid="subscription-status-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Current Plan</div>
            <div className="mt-1 flex items-center gap-2">
              <div className="font-heading font-bold text-2xl">{sub.plan?.name || sub.status?.toUpperCase() || "No plan"}</div>
              <Badge variant={statusColor}>{sub.status?.toUpperCase()}</Badge>
            </div>
            {sub.status === "trial" && sub.days_remaining !== undefined && (
              <div className="mt-2 text-sm text-orange-600 font-medium flex items-center gap-1">
                <Sparkles size={14} /> Your free trial ends in {sub.days_remaining} day{sub.days_remaining !== 1 ? "s" : ""}
              </div>
            )}
            {sub.status === "active" && sub.next_billing_date && (
              <div className="mt-2 text-sm text-muted-foreground">Renews on {sub.next_billing_date.slice(0, 10)}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Price</div>
            <div className="font-heading font-bold text-2xl mt-1">{sub.plan?.price_inr === 0 ? "Free" : inr(sub.plan?.price_inr || 0)}</div>
            <div className="text-xs text-muted-foreground">{sub.plan?.billing === "monthly" ? "per month" : sub.plan?.billing || ""}</div>
          </div>
        </div>
        {(sub.status === "trial" || sub.status === "active") && (
          <div className="mt-4 flex gap-2">
            {sub.status !== "active" && (
              <Button onClick={() => subscribe("billpro")} disabled={processing} data-testid="upgrade-btn">
                {processing ? <Loader2 size={14} className="animate-spin mr-1" /> : <ShieldCheck size={14} className="mr-1" />}
                Upgrade to TheBillPro (₹99/mo)
              </Button>
            )}
            {sub.status === "active" && (
              <Button variant="outline" onClick={cancel} data-testid="cancel-btn">Cancel Subscription</Button>
            )}
          </div>
        )}
      </Card>

      {/* Plan comparison */}
      <div>
        <div className="font-heading font-bold text-lg mb-3">Change plan</div>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p) => (
            <Card key={p.id} className={`p-5 ${sub.plan?.id === p.id ? "border-primary ring-2 ring-primary/10" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="font-heading font-bold text-lg">{p.name}</div>
                {p.featured && <Badge>Popular</Badge>}
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-heading font-bold text-2xl">{p.price_inr === 0 ? "Free" : inr(p.price_inr)}</span>
                {p.price_inr > 0 && <span className="text-xs text-muted-foreground">/mo</span>}
              </div>
              <div className="text-sm text-muted-foreground mt-1">{p.description}</div>
              <Button
                className="w-full mt-4"
                variant={sub.plan?.id === p.id ? "outline" : p.featured ? "default" : "outline"}
                disabled={sub.plan?.id === p.id || p.price_inr === 0 || processing}
                onClick={() => subscribe(p.code)}
                data-testid={`sub-plan-${p.code}`}
              >
                {sub.plan?.id === p.id ? "Current Plan" : p.price_inr === 0 ? "Trial only" : "Subscribe"}
              </Button>
            </Card>
          ))}
        </div>
      </div>

      {/* Payment history */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b font-heading font-bold">Payment History</div>
        {payments.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">No payments yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Order ID</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payment ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{(p.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="font-mono text-xs">{p.order_id}</TableCell>
                  <TableCell>{p.plan_code}</TableCell>
                  <TableCell>{inr(p.amount)}</TableCell>
                  <TableCell>
                    <Badge variant={p.status === "successful" ? "default" : p.status === "failed" ? "destructive" : "secondary"}>
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{p.payment_id || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
